<template>
  <div v-if="Object.keys(tareasPorSemanaFiltradas).length">
    <!-- Botón global para colapsar/desplegar todas las semanas -->
    <div class="global-toggle">
      <button @click="toggleAllSemanas" class="btn btn-outline-secondary btn-sm">
        <font-awesome-icon :icon="todasColapsadas ? 'fas fa-chevron-down' : 'fas fa-chevron-up'" class="me-1" />
        {{ todasColapsadas ? 'Desplegar Todo' : 'Colapsar Todo' }}
      </button>
    </div>
    <div v-for="(tareas, semana) in tareasPorSemanaFiltradas" :key="semana" class="week-card">
      <!-- Encabezado de la Semana -->
      <div class="week-header" @click="toggleColapsar(semana)">
        <div class="header-left">
          <span class="week-title">Semana {{ semana }}</span>
          <!-- Icono minimalista con tooltip de fechas de la semana -->
          <span class="week-range" :title="formatRange(obtenerFechasSemana(semana))">
            <font-awesome-icon icon="fas fa-calendar-alt" />
          </span>
        </div>
        <div class="header-right">
          <span class="badge badge-cost">{{ calcularCostoTotal(semana) }} $</span>
          <span class="badge badge-duration">{{ calcularDuracionTotal(semana) }} h</span>
          <span class="badge badge-tasks">{{ tareas.length }} Tareas</span>
          <font-awesome-icon 
            :icon="isSemanaColapsada(semana) ? 'fas fa-chevron-down' : 'fas fa-chevron-up'" 
            class="collapse-icon" />
        </div>
      </div>
  
      <!-- Lista de Tareas -->
      <transition name="collapse">
        <div v-if="!isSemanaColapsada(semana)" class="tasks-container">
          <div v-for="tarea in tareas" :key="tarea.id" class="task-col">
            <TaskCard :tarea="tarea" @eliminar="eliminarTarea" />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import TaskCard from './TaskCard.vue';

export default {
  props: {
    semanasColapsadas: {
      type: Array,
      required: true,
    },
    tareasPorSemanaFiltradas: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localSemanasColapsadas: [],
    };
  },
  computed: {
    todasColapsadas() {
      return Object.keys(this.tareasPorSemanaFiltradas).length > 0 &&
        this.localSemanasColapsadas.length === Object.keys(this.tareasPorSemanaFiltradas).length;
    },
  },
  watch: {
    tareasPorSemanaFiltradas: {
      immediate: true,
      handler() {
        this.localSemanasColapsadas = [];
      },
    },
  },
  components: {
    TaskCard,
  },
  methods: {
    toggleColapsar(semana) {
      if (this.localSemanasColapsadas.includes(semana)) {
        this.localSemanasColapsadas = this.localSemanasColapsadas.filter(s => s !== semana);
      } else {
        this.localSemanasColapsadas.push(semana);
      }
      this.$emit('update:semanasColapsadas', this.localSemanasColapsadas);
    },
    isSemanaColapsada(semana) {
      return this.localSemanasColapsadas.includes(semana);
    },
    toggleAllSemanas() {
      if (this.todasColapsadas) {
        this.localSemanasColapsadas = [];
      } else {
        this.localSemanasColapsadas = Object.keys(this.tareasPorSemanaFiltradas);
      }
      this.$emit('update:semanasColapsadas', this.localSemanasColapsadas);
    },
    eliminarTarea(tareaId) {
      this.$emit('eliminar', tareaId);
    },
    calcularCostoTotal(semana) {
      const tareas = this.tareasPorSemanaFiltradas[semana];
      return tareas.reduce((total, tarea) => total + (tarea.costo_final_usd || 0), 0).toFixed(2);
    },
    calcularDuracionTotal(semana) {
      const tareas = this.tareasPorSemanaFiltradas[semana];
      return tareas.reduce((total, tarea) => total + (tarea.duracion_horas || 0), 0).toFixed(2);
    },
    obtenerFechasSemana(semana) {
      const tareas = this.tareasPorSemanaFiltradas[semana];
      if (!tareas || tareas.length === 0) return { inicio: '', fin: '' };
      const sorted = tareas.slice().sort((a, b) => new Date(a.fecha_creacion) - new Date(b.fecha_creacion));
      const inicio = sorted[0].fecha_creacion;
      const fin = sorted[sorted.length - 1].fecha_creacion;
      const options = { day: '2-digit', month: 'short', year: 'numeric' };
      return {
        inicio: new Intl.DateTimeFormat('es-ES', options).format(new Date(inicio)),
        fin: new Intl.DateTimeFormat('es-ES', options).format(new Date(fin)),
      };
    },
    formatRange({ inicio, fin }) {
      return `Inicio: ${inicio} - Fin: ${fin}`;
    },
  },
};
</script>

<style scoped>
.week-card {
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 15px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  margin-bottom: 1rem;
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

/* Encabezado de la semana */
.week-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f9f9f9;
  border-bottom: 1px solid #e3e3e3;
  cursor: pointer;
  transition: background-color 0.3s;
}

.week-header:hover {
  background-color: #f1f1f1;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.week-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

/* Nuevo elemento minimalista para rango de fechas */
.week-range {
  font-size: 0.9rem;
  color: #666;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 500;
}

.badge-cost {
  background-color: #28a745;
  color: #fff;
}

.badge-duration {
  background-color: #17a2b8;
  color: #fff;
}

.badge-tasks {
  background-color: var(--app-primary);
  color: #fff;
}

.collapse-icon {
  font-size: 1.25rem;
  color: #6c757d;
}

/* Lista de Tareas */
.tasks-container {
  padding: 1rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1rem;
}

.task-col {
  margin-bottom: 1rem;
}

/* Transición para colapsar/expandir */
.collapse-enter-active, .collapse-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
}
.collapse-enter, .collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Botón global para toggle de todas las semanas */
.global-toggle {
  text-align: right;
  margin-bottom: 0.5rem;
}
.global-toggle .btn {
  font-size: 0.85rem;
  padding: 0.3rem 0.6rem;
}

@media (max-width: 768px) {
  .week-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-right {
    justify-content: space-between;
    width: 100%;
  }

  .tasks-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .week-title {
    font-size: 1rem;
  }

  .badge {
    font-size: 0.75rem;
    padding: 0.2rem 0.5rem;
  }

  .global-toggle .btn {
    font-size: 0.75rem;
    padding: 0.2rem 0.4rem;
  }
}
</style>
